import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/utils/cn'
import { Sun, Moon, MessageCircle, BookOpen, Library, Settings, LogOut, ChevronDown, MessageSquare } from 'lucide-react'
import { FeedbackModal } from '@/components/ui/FeedbackModal'

interface HeaderProps {
  currentPage?: 'feed' | 'chat' | 'blog' | 'biblioteca' // | 'marketplace'
  title?: string
}

export function Header({ currentPage = 'chat' }: HeaderProps) {
  const { user, logout } = useAuthStore()
  const { isDark, toggleTheme } = useTheme()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  const handleLogout = () => {
    logout()
    setUserMenuOpen(false)
  }

  const navItems = [
    { href: '/chat', label: 'Chat', page: 'chat' as const, icon: MessageCircle },
    { href: '/blog', label: 'Blog', page: 'blog' as const, icon: BookOpen },
    { href: '/biblioteca', label: 'Biblioteca', page: 'biblioteca' as const, icon: Library },
    // { href: '/marketplace', label: 'Marketplace', page: 'marketplace' as const, icon: ShoppingBag },
  ]

  return (
    <>
      <header className="bg-brand-surface border-b border-brand-border transition-colors duration-300">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Logo */}
            <a href="/chat" className="flex items-center gap-2.5 flex-shrink-0">
              <img
                src={isDark ? '/assets/logos/Logotipo-Verde-Broto.svg' : '/assets/logos/Logotipo-Verde.svg'}
                alt="RegenerAI"
                className="h-14 w-auto"
              />
            </a>

            {/* Center: Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map(({ href, label, page, icon: Icon }) => (
                <a
                  key={page}
                  href={href}
                  {...(href.startsWith('http') && { target: '_blank', rel: 'noopener noreferrer' })}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200",
                    currentPage === page
                      ? "bg-brand-primary-light text-brand-primary"
                      : "text-brand-text-secondary hover:bg-brand-surface-alt hover:text-brand-text"
                  )}
                >
                  <Icon size={22} />
                  {label}
                </a>
              ))}
            </nav>

            {/* Right: Theme Toggle + User Menu */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl text-brand-text-secondary hover:bg-brand-surface-alt transition-all duration-200"
                aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
              >
                {isDark ? <Sun size={22} /> : <Moon size={22} />}
              </button>

              {/* User Menu */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-1.5 pr-3 rounded-xl hover:bg-brand-surface-alt transition-all duration-200"
                  >
                    <div className="w-9 h-9 bg-brand-accent rounded-full flex items-center justify-center overflow-hidden">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            e.currentTarget.nextElementSibling?.classList.remove('hidden')
                          }}
                        />
                      ) : null}
                      <span className={`text-verde-500 text-sm font-bold ${user.avatarUrl ? 'hidden' : ''}`}>
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-brand-text">
                      {user.name?.split(' ')[0]}
                    </span>
                    <ChevronDown size={16} className="hidden sm:block text-brand-text-muted" />
                  </button>

                  {/* Dropdown */}
                  {userMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setUserMenuOpen(false)}
                      />

                      <div className="absolute right-0 mt-2 w-56 card shadow-lg z-50 overflow-hidden">
                        <div className="p-4 border-b border-brand-border">
                          <p className="text-sm font-semibold text-brand-text">{user.name}</p>
                          <p className="text-xs text-brand-text-muted mt-0.5">{user.email}</p>
                        </div>
                        <div className="py-1">
                          {navItems.map(({ href, label, icon: Icon }) => (
                            <a
                              key={href}
                              href={href}
                              {...(href.startsWith('http') && { target: '_blank', rel: 'noopener noreferrer' })}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-text-secondary hover:bg-brand-surface-alt transition-colors"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <Icon size={18} />
                              {label}
                            </a>
                          ))}
                          <div className="border-t border-brand-border my-1" />
                          <a
                            href="/settings"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-text-secondary hover:bg-brand-surface-alt transition-colors"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <Settings size={18} />
                            Configurações
                          </a>
                          <button
                            onClick={() => { setFeedbackOpen(true); setUserMenuOpen(false) }}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-brand-text-secondary hover:bg-brand-surface-alt transition-colors"
                          >
                            <MessageSquare size={18} />
                            Enviar Feedback
                          </button>
                          <div className="border-t border-brand-border my-1" />
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                          >
                            <LogOut size={18} />
                            Sair
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <a
                  href="/login"
                  className="px-4 py-2 bg-brand-primary text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Entrar
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </>
  )
}